<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import MdEditor from '../components/MdEditor.vue'
import {
  getPlanList,
  getPlanDetail,
  addPlan as apiAddPlan,
  editPlan as apiEditPlan,
  savePlanContent,
  togglePlan as apiTogglePlan,
  deletePlan as apiDeletePlan,
  PLAN_TYPE_MAP,
  PLAN_TYPE_REVERSE,
  type Plan as ApiPlan,
} from '../api/plan'

// 计划类型
type PlanType = 'week' | 'month' | 'quarter' | 'year'

// 前端视图模型（保持与原模板一致的字段名）
interface Plan {
  id: number
  title: string
  type: PlanType
  startDate: string
  endDate: string
  description: string // 存储 HTML 内容
  completed: boolean
  createdAt: string
}

// 计划分类配置
const planCategories: { key: PlanType; label: string; icon: string }[] = [
  { key: 'week', label: '周计划', icon: '📅' },
  { key: 'month', label: '月计划', icon: '🗓️' },
  { key: 'quarter', label: '季度计划', icon: '📆' },
  { key: 'year', label: '年计划', icon: '🎯' },
]

// 当前选中的分类
const activeType = ref<PlanType>('week')

// 计划列表
const plans = ref<Plan[]>([])

// 时闳戳 -> YYYY-MM-DD
const tsToDate = (ts: number): string => {
  if (!ts) return ''
  const d = new Date(ts * 1000)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// 后端 -> 前端
const mapFromApi = (p: ApiPlan): Plan => ({
  id: p.id,
  title: p.title,
  type: PLAN_TYPE_REVERSE[p.type] || 'week',
  startDate: p.start_date || '',
  endDate: p.end_date || '',
  description: p.content || '',
  completed: p.status === 1,
  createdAt: tsToDate(p.create_at),
})

// 当前分类的计划
const filteredPlans = computed(() =>
  plans.value.filter(p => p.type === activeType.value)
)

// 未完成 / 已完成
const incompletePlans = computed(() => filteredPlans.value.filter(p => !p.completed))
const completedPlans = computed(() => filteredPlans.value.filter(p => p.completed))

// 当前选中的计划
const selectedPlanId = ref<number | null>(null)
const selectedPlan = computed(() =>
  plans.value.find(p => p.id === selectedPlanId.value) || null
)

// 详情描述编辑器内容
const descriptionContent = ref('')
const mdEditorRef = ref<InstanceType<typeof MdEditor> | null>(null)
const isEditorReady = ref(false)

const selectPlan = async (id: number) => {
  selectedPlanId.value = id
  // 如果列表没有详情内容，拉一次详情接口
  const plan = plans.value.find(p => p.id === id)
  if (plan && !plan.description) {
    try {
      const res = await getPlanDetail(id)
      if (res.code === 1 && res.data) {
        plan.description = res.data.content || ''
        descriptionContent.value = plan.description
      }
    } catch (e) {
      console.error('加载详情失败', e)
    }
  }
}

// 切换计划时，加载描述到编辑器
watch(selectedPlanId, (newId) => {
  isEditorReady.value = false
  if (newId !== null) {
    const plan = plans.value.find(p => p.id === newId)
    descriptionContent.value = plan?.description || ''
  } else {
    descriptionContent.value = ''
  }
  nextTick(() => {
    setTimeout(() => {
      isEditorReady.value = true
    }, 100)
  })
})

// 编辑器内容变化，防抖同步到本地 plans【仅同步到内存，不写入后端】
let saveTimeout: number | null = null
watch(descriptionContent, (newValue) => {
  if (!isEditorReady.value) return
  if (selectedPlanId.value === null) return
  if (saveTimeout) clearTimeout(saveTimeout)
  saveTimeout = window.setTimeout(() => {
    const plan = plans.value.find(p => p.id === selectedPlanId.value)
    if (plan) plan.description = newValue
  }, 1000)
})

// 手动保存详情
const isSaving = ref(false)
const saveDetail = async () => {
  if (!selectedPlan.value || isSaving.value) return
  isSaving.value = true
  try {
    selectedPlan.value.description = descriptionContent.value
    const res = await savePlanContent(selectedPlan.value.id, descriptionContent.value)
    if (res.code !== 1) {
      alert(res.message || '保存失败')
    }
  } catch (e) {
    console.error(e)
    alert('保存失败')
  } finally {
    isSaving.value = false
  }
}

// 加载列表
const loadPlans = async () => {
  try {
    const res = await getPlanList({ type: PLAN_TYPE_MAP[activeType.value] })
    if (res.code === 1 && res.data) {
      plans.value = res.data.map(mapFromApi)
    }
  } catch (e) {
    console.error('加载计划列表失败', e)
  }
}

onMounted(() => {
  loadPlans()
})

// 切换分类
const switchType = (type: PlanType) => {
  activeType.value = type
  selectedPlanId.value = null
  loadPlans()
}

// 切换完成状态
const togglingIds = ref<Set<number>>(new Set())
const isToggling = (id: number) => togglingIds.value.has(id)

const togglePlanComplete = async (plan: Plan) => {
  if (togglingIds.value.has(plan.id)) return
  // 赋值新 Set 以触发响应式更新
  togglingIds.value = new Set(togglingIds.value).add(plan.id)
  try {
    const res = await apiTogglePlan(plan.id)
    if (res.code === 1) {
      plan.completed = !plan.completed
    } else {
      alert(res.message || '操作失败')
    }
  } catch (e) {
    console.error(e)
  } finally {
    const next = new Set(togglingIds.value)
    next.delete(plan.id)
    togglingIds.value = next
  }
}

// ===== 添加 / 编辑信息弹窗（只含标题/类型/起止日期，描述在右侧编辑器中编辑）=====
const showFormModal = ref(false)
const isEditing = ref(false)
const isSubmitting = ref(false)
const formData = ref<Plan>({
  id: 0,
  title: '',
  type: 'week',
  startDate: '',
  endDate: '',
  description: '',
  completed: false,
  createdAt: '',
})

const formatDate = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// 根据类型获取默认起止日期
const getDefaultDateRange = (type: PlanType): { start: string; end: string } => {
  const now = new Date()
  const start = new Date(now)
  const end = new Date(now)

  if (type === 'week') {
    const day = now.getDay() || 7
    start.setDate(now.getDate() - day + 1)
    end.setDate(start.getDate() + 6)
  } else if (type === 'month') {
    start.setDate(1)
    end.setMonth(now.getMonth() + 1, 0)
  } else if (type === 'quarter') {
    const q = Math.floor(now.getMonth() / 3)
    start.setMonth(q * 3, 1)
    end.setMonth(q * 3 + 3, 0)
  } else if (type === 'year') {
    start.setMonth(0, 1)
    end.setMonth(11, 31)
  }
  return { start: formatDate(start), end: formatDate(end) }
}

const openAddModal = () => {
  isEditing.value = false
  const range = getDefaultDateRange(activeType.value)
  formData.value = {
    id: 0,
    title: '',
    type: activeType.value,
    startDate: range.start,
    endDate: range.end,
    description: '',
    completed: false,
    createdAt: '',
  }
  showFormModal.value = true
}

const openEditModal = (plan: Plan) => {
  isEditing.value = true
  formData.value = { ...plan }
  showFormModal.value = true
}

const closeFormModal = () => {
  showFormModal.value = false
}

const onTypeFormChange = (e: Event) => {
  const target = e.target as HTMLSelectElement
  const newType = target.value as PlanType
  formData.value.type = newType
  if (!isEditing.value) {
    const range = getDefaultDateRange(newType)
    formData.value.startDate = range.start
    formData.value.endDate = range.end
  }
}

const submitForm = async () => {
  if (isSubmitting.value) return
  if (!formData.value.title.trim()) {
    alert('请输入计划标题')
    return
  }
  if (!formData.value.startDate || !formData.value.endDate) {
    alert('请选择起止日期')
    return
  }

  const payload = {
    title: formData.value.title.trim(),
    type: PLAN_TYPE_MAP[formData.value.type],
    start_date: formData.value.startDate,
    end_date: formData.value.endDate,
  }

  isSubmitting.value = true
  try {
    if (isEditing.value) {
      const res = await apiEditPlan({ id: formData.value.id, ...payload })
      if (res.code !== 1) {
        alert(res.message || '保存失败')
        return
      }
      const idx = plans.value.findIndex(p => p.id === formData.value.id)
      if (idx >= 0) {
        // 保留已加载的 description
        plans.value[idx] = {
          ...formData.value,
          description: plans.value[idx].description,
        }
      }
      // 如果修改了类型，需重新加载列表
      if (formData.value.type !== activeType.value) {
        await loadPlans()
      }
    } else {
      const res = await apiAddPlan(payload)
      if (res.code !== 1 || !res.data) {
        alert(res.message || '创建失败')
        return
      }
      // 切换到新计划的分类并重新加载
      activeType.value = formData.value.type
      await loadPlans()
      selectedPlanId.value = res.data.id
    }
    showFormModal.value = false
  } catch (e) {
    console.error(e)
    alert('操作失败')
  } finally {
    isSubmitting.value = false
  }
}

// ===== 删除确认 =====
const showDeleteModal = ref(false)
const deletingPlan = ref<Plan | null>(null)
const isDeleting = ref(false)

const openDeleteModal = (plan: Plan) => {
  deletingPlan.value = plan
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  if (isDeleting.value) return
  showDeleteModal.value = false
  deletingPlan.value = null
}

const confirmDelete = async () => {
  if (!deletingPlan.value || isDeleting.value) return
  const id = deletingPlan.value.id
  isDeleting.value = true
  try {
    const res = await apiDeletePlan(id)
    if (res.code !== 1) {
      alert(res.message || '删除失败')
      return
    }
    plans.value = plans.value.filter(p => p.id !== id)
    if (selectedPlanId.value === id) {
      selectedPlanId.value = null
    }
    showDeleteModal.value = false
    deletingPlan.value = null
  } catch (e) {
    console.error(e)
    alert('删除失败')
  } finally {
    isDeleting.value = false
  }
}

// ===== 右键菜单 =====
const contextMenu = ref({
  show: false,
  x: 0,
  y: 0,
  plan: null as Plan | null,
})

const showContextMenu = (e: MouseEvent, plan: Plan) => {
  e.preventDefault()
  e.stopPropagation()
  contextMenu.value = { show: true, x: e.clientX, y: e.clientY, plan }
}

const hideContextMenu = () => {
  contextMenu.value.show = false
  contextMenu.value.plan = null
}

const onContextEdit = () => {
  if (contextMenu.value.plan) openEditModal(contextMenu.value.plan)
  hideContextMenu()
}

const onContextDelete = () => {
  if (contextMenu.value.plan) openDeleteModal(contextMenu.value.plan)
  hideContextMenu()
}

const getTypeLabel = (type: PlanType) =>
  planCategories.find(c => c.key === type)?.label || ''
</script>

<template>
  <div class="plans-page">
    <!-- 左侧：分类 + 列表 -->
    <aside class="plans-sidebar">
      <!-- 分类切换 -->
      <div class="category-tabs">
        <div
          v-for="cat in planCategories"
          :key="cat.key"
          class="category-tab"
          :class="{ active: activeType === cat.key }"
          @click="switchType(cat.key)"
        >
          <span class="cat-icon">{{ cat.icon }}</span>
          <span class="cat-label">{{ cat.label }}</span>
        </div>
      </div>

      <!-- 列表头 -->
      <div class="list-header">
        <span class="list-title">{{ getTypeLabel(activeType) }}列表</span>
        <button class="btn-add" @click="openAddModal" title="新增计划">+</button>
      </div>

      <!-- 计划列表 -->
      <div class="plan-list">
        <!-- 未完成 -->
        <div
          v-for="plan in incompletePlans"
          :key="plan.id"
          class="plan-item"
          :class="{ active: selectedPlanId === plan.id }"
          @click="selectPlan(plan.id)"
          @contextmenu="showContextMenu($event, plan)"
        >
          <div class="plan-item-checkbox" :class="{ loading: isToggling(plan.id) }" @click.stop="togglePlanComplete(plan)">
            <div v-if="isToggling(plan.id)" class="checkbox-spinner"></div>
            <div v-else class="checkbox-circle"></div>
          </div>
          <div class="plan-item-content">
            <div class="plan-item-title">{{ plan.title }}</div>
            <div class="plan-item-meta">
              {{ plan.startDate }} ~ {{ plan.endDate }}
            </div>
          </div>
        </div>

        <!-- 已完成 -->
        <div v-if="completedPlans.length > 0" class="completed-divider">
          已完成 {{ completedPlans.length }}
        </div>
        <div
          v-for="plan in completedPlans"
          :key="plan.id"
          class="plan-item completed"
          :class="{ active: selectedPlanId === plan.id }"
          @click="selectPlan(plan.id)"
          @contextmenu="showContextMenu($event, plan)"
        >
          <div class="plan-item-checkbox" :class="{ loading: isToggling(plan.id) }" @click.stop="togglePlanComplete(plan)">
            <div v-if="isToggling(plan.id)" class="checkbox-spinner"></div>
            <div v-else class="checkbox-circle checked"><span class="checkmark">✓</span></div>
          </div>
          <div class="plan-item-content">
            <div class="plan-item-title">{{ plan.title }}</div>
            <div class="plan-item-meta">
              {{ plan.startDate }} ~ {{ plan.endDate }}
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="filteredPlans.length === 0" class="empty-list">
          <div class="empty-emoji">📋</div>
          <p>暂无{{ getTypeLabel(activeType) }}</p>
          <button class="btn-create" @click="openAddModal">+ 新建计划</button>
        </div>
      </div>
    </aside>

    <!-- 右侧：详情 -->
    <main class="plan-detail">
      <div v-if="selectedPlan" class="detail-card">
        <div class="detail-header">
          <div class="detail-title-row">
            <span class="detail-type-badge">{{ getTypeLabel(selectedPlan.type) }}</span>
            <h2 class="detail-title">{{ selectedPlan.title }}</h2>
          </div>
          <div class="detail-actions">
            <button class="btn-action edit" @click="openEditModal(selectedPlan)">
              编辑信息
            </button>
            <button class="btn-action delete" @click="openDeleteModal(selectedPlan)">
              删除
            </button>
            <button class="btn-action save" @click="saveDetail" :disabled="isSaving">
              <span v-if="isSaving" class="btn-spinner"></span>
              {{ isSaving ? '保存中...' : '保存' }}
            </button>
          </div>
        </div>

        <div class="detail-meta">
          <div class="meta-item">
            <span class="meta-label">起止日期</span>
            <span class="meta-value">{{ selectedPlan.startDate }} ~ {{ selectedPlan.endDate }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">状态</span>
            <span class="meta-value status" :class="{ done: selectedPlan.completed }">
              {{ selectedPlan.completed ? '已完成' : '进行中' }}
            </span>
          </div>
          <div class="meta-item">
            <span class="meta-label">创建于</span>
            <span class="meta-value">{{ selectedPlan.createdAt }}</span>
          </div>
        </div>

        <div class="detail-section editor-section">
          <div class="editor-wrap">
            <MdEditor
              ref="mdEditorRef"
              v-model="descriptionContent"
              placeholder="在此详细记录该计划的目标、关键节点、执行步骤等内容..."
              :showSaveButton="false"
              :noMaxWidth="true"
              uploadPath="/api/upload/plan"
            />
          </div>
        </div>
      </div>

      <div v-else class="empty-detail">
        <div class="empty-icon-big">📝</div>
        <p>请从左侧选择一个计划查看详情</p>
        <p class="hint">或点击 "+" 新建一个{{ getTypeLabel(activeType) }}</p>
      </div>
    </main>

    <!-- 添加/编辑信息弹窗（只含基本信息，描述在右侧编辑器中编辑） -->
    <div class="modal-overlay" v-if="showFormModal" @click.self="closeFormModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ isEditing ? '编辑计划信息' : '新建计划' }}</h3>
          <button class="close-btn" @click="closeFormModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>计划标题</label>
            <input
              type="text"
              v-model="formData.title"
              class="form-input"
              placeholder="请输入计划标题"
            />
          </div>
          <div class="form-row-3">
            <div class="form-group">
              <label>计划类型</label>
              <select :value="formData.type" @change="onTypeFormChange" class="form-select">
                <option v-for="cat in planCategories" :key="cat.key" :value="cat.key">
                  {{ cat.label }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>开始日期</label>
              <input type="date" v-model="formData.startDate" class="form-input" />
            </div>
            <div class="form-group">
              <label>结束日期</label>
              <input type="date" v-model="formData.endDate" class="form-input" />
            </div>
          </div>
          <p class="form-hint">提示：详细描述请在右侧编辑器中编写，支持富文本、图片等。</p>
        </div>
        <div class="modal-footer">
          <button class="btn ghost" @click="closeFormModal" :disabled="isSubmitting">取消</button>
          <button class="btn primary" @click="submitForm" :disabled="isSubmitting">
            <span v-if="isSubmitting" class="btn-spinner"></span>
            {{ isSubmitting ? (isEditing ? '保存中...' : '创建中...') : (isEditing ? '保存' : '创建') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 删除确认弹窗 -->
    <div class="modal-overlay" v-if="showDeleteModal" @click.self="closeDeleteModal">
      <div class="modal-content small-modal">
        <div class="modal-header">
          <h3>删除计划</h3>
          <button class="close-btn" @click="closeDeleteModal" :disabled="isDeleting">×</button>
        </div>
        <div class="modal-body">
          <p class="confirm-text">确定要删除“{{ deletingPlan?.title }}”吗？该操作无法恢复。</p>
        </div>
        <div class="modal-footer">
          <button class="btn ghost" @click="closeDeleteModal" :disabled="isDeleting">取消</button>
          <button class="btn danger" @click="confirmDelete" :disabled="isDeleting">
            <span v-if="isDeleting" class="btn-spinner"></span>
            {{ isDeleting ? '删除中...' : '确定删除' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 右键菜单 -->
    <div
      v-if="contextMenu.show"
      class="context-menu-overlay"
      @click="hideContextMenu"
      @contextmenu.prevent="hideContextMenu"
    >
      <div
        class="context-menu"
        :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      >
        <div class="context-menu-item" @click="onContextEdit">
          <span>✏️</span><span>编辑</span>
        </div>
        <div class="context-menu-item delete" @click="onContextDelete">
          <span>🗑️</span><span>删除</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.plans-page {
  display: flex;
  height: 100%;
  background: #f5f5f5;
}

/* 左侧：分类 + 列表 */
.plans-sidebar {
  width: 360px;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.category-tabs {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
}

.category-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.7rem 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  background: #f5f5f5;
  color: #666;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
  user-select: none;
}

.category-tab:hover {
  background: #ebebeb;
  color: #333;
}

.category-tab.active {
  background: #42b883;
  color: #fff;
  box-shadow: 0 2px 6px rgba(66, 184, 131, 0.3);
}

.cat-icon {
  font-size: 1rem;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.9rem 1.25rem;
  border-bottom: 1px solid #f0f0f0;
}

.list-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
}

.btn-add {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background: #42b883;
  color: #fff;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-add:hover {
  background: #369970;
  transform: scale(1.05);
}

.plan-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
}

.plan-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.25rem;
  cursor: pointer;
  border-left: 3px solid transparent;
  transition: all 0.2s;
}

.plan-item:hover {
  background: #f8f8f8;
}

.plan-item.active {
  background: #e8f5ee;
  border-left-color: #42b883;
}

.plan-item.completed .plan-item-title {
  color: #999;
  text-decoration: line-through;
}

.plan-item-checkbox {
  flex-shrink: 0;
}

.plan-item-checkbox.loading {
  pointer-events: none;
}

.checkbox-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e0e0e0;
  border-top-color: #42b883;
  border-radius: 50%;
  animation: btn-spin 0.7s linear infinite;
  box-sizing: border-box;
}

.checkbox-circle {
  width: 20px;
  height: 20px;
  border: 2px solid #d0d0d0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  transition: all 0.2s;
  cursor: pointer;
}

.checkbox-circle:hover {
  border-color: #42b883;
}

.checkbox-circle.checked {
  background: #42b883;
  border-color: #42b883;
}

.checkmark {
  color: #fff;
  font-size: 12px;
  font-weight: bold;
}

.plan-item-content {
  flex: 1;
  min-width: 0;
}

.plan-item-title {
  font-size: 0.95rem;
  color: #333;
  font-weight: 500;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.plan-item-meta {
  font-size: 0.78rem;
  color: #999;
}

.completed-divider {
  padding: 0.5rem 1.25rem;
  font-size: 0.8rem;
  color: #999;
  background: #f8f8f8;
  margin: 0.5rem 0;
}

.empty-list {
  padding: 3rem 1.5rem;
  text-align: center;
  color: #999;
}

.empty-emoji {
  font-size: 3rem;
  margin-bottom: 0.75rem;
}

.empty-list p {
  margin: 0 0 1rem;
  font-size: 0.9rem;
}

.btn-create {
  padding: 0.5rem 1.25rem;
  border: 1px dashed #42b883;
  background: transparent;
  color: #42b883;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.btn-create:hover {
  background: #42b883;
  color: #fff;
}

/* 右侧详情 */
.plan-detail {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  min-height: 0;
}

.detail-card {
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  min-height: calc(100% - 0px);
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 1.5rem;
}

.detail-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.detail-type-badge {
  padding: 0.25rem 0.75rem;
  background: #e8f5ee;
  color: #42b883;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.detail-title {
  margin: 0;
  font-size: 1.4rem;
  color: #222;
  font-weight: 600;
  line-height: 1.4;
}

.detail-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.btn-action {
  padding: 0.5rem 1rem;
  border: 1px solid #e0e0e0;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  color: #666;
  transition: all 0.2s;
}

.btn-action.edit:hover {
  border-color: #42b883;
  color: #42b883;
}

.btn-action.delete:hover {
  border-color: #f44336;
  color: #f44336;
}

.btn-action.save {
  background: #42b883;
  color: #fff;
  border-color: #42b883;
  font-weight: 600;
}

.btn-action.save:hover:not(:disabled) {
  background: #369970;
  border-color: #369970;
}

.btn-action.save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.detail-meta {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding: 1.25rem;
  background: #fafafa;
  border-radius: 10px;
  margin-bottom: 1.5rem;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.meta-label {
  font-size: 0.78rem;
  color: #999;
}

.meta-value {
  font-size: 0.95rem;
  color: #333;
  font-weight: 500;
}

.meta-value.status {
  color: #ff9800;
}

.meta-value.status.done {
  color: #42b883;
}

.detail-section {
  margin-top: 1.5rem;
}

.editor-section {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
}

.editor-wrap {
  background: #fff;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 280px);
  overflow: visible;
}

.editor-wrap :deep(.md-editor) {
  border: none;
  border-radius: 0;
  flex: none;
  min-height: calc(100vh - 280px);
  display: flex;
  flex-direction: column;
}

/* 让编辑器内容区随内容高度自适应，不内部滚动 */
.editor-wrap :deep(.editor-content) {
  flex: 1;
  min-height: 0;
  overflow: visible;
}

.editor-wrap :deep(.ProseMirror) {
  min-height: calc(100vh - 340px);
  outline: none;
}

.section-title {
  margin: 0 0 0.75rem;
  font-size: 1rem;
  color: #333;
  font-weight: 600;
}

.detail-description {
  font-size: 0.95rem;
  color: #444;
  line-height: 1.8;
  white-space: pre-wrap;
  background: #fafafa;
  padding: 1.25rem;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  min-height: 120px;
}

.empty-detail {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  text-align: center;
}

.empty-icon-big {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.6;
}

.empty-detail p {
  margin: 0.25rem 0;
  font-size: 0.95rem;
}

.empty-detail .hint {
  font-size: 0.85rem;
  color: #bbb;
}

/* 弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 14px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
}

.small-modal {
  max-width: 420px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.75rem;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
  font-weight: 600;
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #999;
  font-size: 1.5rem;
  cursor: pointer;
  border-radius: 4px;
}

.close-btn:hover {
  background: #f0f0f0;
  color: #333;
}

.modal-body {
  padding: 1.5rem 1.75rem;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.75rem;
  border-top: 1px solid #eee;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  font-size: 0.88rem;
  color: #555;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-row-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.form-row-3 .form-group {
  margin-bottom: 0;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  background: #fafafa;
  box-sizing: border-box;
  font-family: inherit;
  transition: all 0.2s;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #42b883;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(66, 184, 131, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
  line-height: 1.6;
}

.form-hint {
  margin: 0;
  padding: 0.75rem 1rem;
  background: #f5fbf8;
  border: 1px dashed #b9e3cd;
  border-radius: 8px;
  color: #42b883;
  font-size: 0.85rem;
  line-height: 1.6;
}

.btn {
  padding: 0.65rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn.ghost {
  background: #f2f2f2;
  color: #666;
}

.btn.ghost:hover {
  background: #e0e0e0;
}

.btn.primary {
  background: #42b883;
  color: #fff;
}

.btn.primary:hover:not(:disabled) {
  background: #369970;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.btn.primary:disabled {
  background: #42b883;
}

/* 加载小轮子 */
.btn-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 6px;
  vertical-align: -2px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: btn-spin 0.7s linear infinite;
}

@keyframes btn-spin {
  to { transform: rotate(360deg); }
}

.btn.danger {
  background: #f44336;
  color: #fff;
}

.btn.danger:hover {
  background: #d32f2f;
}

.btn.danger:hover:not(:disabled) {
  background: #d32f2f;
}

.btn.danger:disabled {
  background: #f44336;
}

.confirm-text {
  margin: 0;
  font-size: 0.95rem;
  color: #444;
  line-height: 1.6;
}

/* 右键菜单 */
.context-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
}

.context-menu {
  position: fixed;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 6px 0;
  min-width: 130px;
  z-index: 9999;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #333;
  transition: background 0.2s;
}

.context-menu-item:hover {
  background: #f5f5f5;
}

.context-menu-item.delete:hover {
  background: #fef0f0;
  color: #f44336;
}
</style>
